import { NgClass, UpperCasePipe, isPlatformBrowser } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  PLATFORM_ID,
  computed,
  inject,
  output,
} from '@angular/core'
import { Router, RouterLink } from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'

import { AuthService } from '../../../../services/auth/auth.service'
import type { DialogState } from '../../../../services/dialog/dialog.service'
import { DialogService } from '../../../../services/dialog/dialog.service'
import type { SupportedLanguage } from '../../../../services/internationalization.service'
import { InternationalizationService } from '../../../../services/internationalization.service'
import { ClickOutsideDirective } from '../../../../shared/directives/click-outside.directive'
import { ProfileIconComponent } from '../profile-icon/profile-icon.component'

@Component({
  selector: 'dhp-desktop-actions',
  templateUrl: './desktop-actions.component.html',
  imports: [
    ClickOutsideDirective,
    RouterLink,
    UpperCasePipe,
    TranslatePipe,
    ProfileIconComponent,
    NgClass,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DesktopActionsComponent {
  private router = inject(Router)
  private platformId = inject(PLATFORM_ID)
  private authService = inject(AuthService)
  private i18nService = inject(InternationalizationService)
  dialogService = inject(DialogService)
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() currentLanguage: SupportedLanguage = 'nl'
  readonly searchStateChange = output<boolean>()
  readonly languageChange = output<SupportedLanguage>()
  isAuthenticated = computed(() => !!this.authService.currentUser())
  currentUser = computed(() => this.authService.currentUser())

  get userInitial(): string | null {
    const user = this.authService.currentUser()
    return user?.name ? user.name.charAt(0).toUpperCase() : null
  }

  get userName(): string | null {
    return this.authService.currentUser()?.name ?? null
  }

  get userEmail(): string | null {
    return this.authService.currentUser()?.email ?? null
  }

  get organization(): string | null {
    return this.authService.currentUser()?.organization?.name ?? null
  }

  get getReturnUrl(): string {
    if (isPlatformBrowser(this.platformId)) {
      return window.location.pathname
    }

    return this.router.url || '/'
  }

  enabledLanguages(): SupportedLanguage[] {
    return this.i18nService.enabledLanguages()
  }

  toggleDropdown(dropdownKey: keyof DialogState, event?: MouseEvent): void {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    this.dialogService.toggleDropdown(dropdownKey)
  }

  selectLanguage(lang: SupportedLanguage): void {
    this.currentLanguage = lang
    this.languageChange.emit(lang)
    this.dialogService.closeAll()
  }

  onSearchStateChange(isExpanded: boolean): void {
    this.searchStateChange.emit(isExpanded)
  }

  async logout(): Promise<void> {
    await this.authService.logout()
    this.dialogService.closeAll()
    await this.router.navigate(['/'])
  }

  isDropdownOpen(dropdownKey: keyof DialogState): boolean {
    return this.dialogService.isOpen(dropdownKey)
  }

  protected navigateProfile(): Promise<boolean> {
    return this.router.navigate(['/organization'])
  }
}
